import React, { useState } from 'react';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { Patient, HealthRecord, VitalHistory } from '../types';
import { parseDateString } from '../utils';
import MonthlyReportPage from '../monthly-report/components/MonthlyReportPage';


interface ExportModalProps {
  isOpen: boolean;
  onClose: () => void;
  patient: Patient;
  records: HealthRecord[];
  vitalHistory: VitalHistory[];
}

const ExportModal: React.FC<ExportModalProps> = ({ isOpen, onClose, patient, records, vitalHistory }) => {
  const [exportType, setExportType] = useState<'records' | 'vitals' | 'monthly'>('records');
  const [dateRange, setDateRange] = useState('all'); // all, 7days, 30days, custom
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [showMonthlyReport, setShowMonthlyReport] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });

  const handleGeneratePdf = () => {
    setIsGenerating(true);

    const doc = new jsPDF() as any;

    const now = new Date();
    let startDate: Date;
    let endDate: Date = now;
    let dateRangeText = 'All Time';

    switch (dateRange) {
      case '7days':
        startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 6);
        dateRangeText = `Last 7 Days (${startDate.toLocaleDateString()} - ${endDate.toLocaleDateString()})`;
        break;
      case '30days':
        startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 29);
        dateRangeText = `Last 30 Days (${startDate.toLocaleDateString()} - ${endDate.toLocaleDateString()})`;
        break;
      case 'custom':
        if (!customStartDate || !customEndDate) {
            alert('Please select a valid custom date range.');
            setIsGenerating(false);
            return;
        }
        startDate = parseDateString(customStartDate);
        endDate = parseDateString(customEndDate);
        endDate.setHours(23, 59, 59, 999); // Include the whole end day
        dateRangeText = `Custom Range (${startDate.toLocaleDateString()} - ${endDate.toLocaleDateString()})`;
        break;
      default: // 'all'
        startDate = new Date(0); // Epoch start
        break;
    }
    
    // Header
    doc.setFontSize(18);
    doc.text('Patient Health Report', 14, 22);
    doc.setFontSize(11);
    doc.text(`Patient: ${patient.name} (ID: ${patient.id})`, 14, 30);
    doc.text(`Date Range: ${dateRangeText}`, 14, 36);
    doc.text(`Generated on: ${new Date().toLocaleString()}`, 14, 42);

    if (exportType === 'records') {
      const filteredRecords = records.filter(r => {
        const recordDate = parseDateString(r.date);
        return recordDate >= startDate && recordDate <= endDate;
      });

      const tableData = filteredRecords.map(r => [
        new Date(r.date).toLocaleString(),
        r.type,
        r.summary,
        r.recordedBy,
      ]);

      autoTable(doc, {
        startY: 50,
        head: [['Date', 'Type', 'Summary', 'Recorded By']],
        body: tableData,
        headStyles: { fillColor: [90, 70, 170] },
        columnStyles: {
          0: { cellWidth: 35 },
          1: { cellWidth: 25 },
          2: { cellWidth: 'auto' },
          3: { cellWidth: 35 },
        },
        styles: { fontSize: 8, cellPadding: 3 },
      });
    } else { // 'vitals'
        const filteredVitals = vitalHistory.filter(v => {
            const vitalDate = parseDateString(v.date);
            vitalDate.setHours(0,0,0,0);
            return vitalDate >= startDate && vitalDate <= endDate;
        });

        // --- Draw 4 vital trend charts (matching web style) ---
        const dataPoints = filteredVitals.length;
        const chartConfigs = [
            { label: 'Blood Pressure', unit: 'mmHg', color: [90, 70, 170] as number[], lightColor: [162, 173, 214] as number[], getValue: (v: VitalHistory) => v.bp.systolic, getValue2: (v: VitalHistory) => v.bp.diastolic },
            { label: 'Heart Rate', unit: 'bpm', color: [239, 68, 68] as number[], getValue: (v: VitalHistory) => v.hr },
            { label: 'Glucose', unit: 'mg/dL', color: [34, 197, 94] as number[], getValue: (v: VitalHistory) => v.glucose },
            { label: 'Weight', unit: 'kg', color: [59, 130, 246] as number[], getValue: (v: VitalHistory) => v.weight },
        ];

        const chartW = 85;
        const chartH = 50;
        let chartStartY = 52;

        if (dataPoints > 1) {
            doc.setFontSize(11);
            doc.setTextColor(60, 60, 60);
            doc.text('Vital Analytics', 14, chartStartY - 2);
            doc.setFontSize(8);
            doc.setTextColor(150, 150, 150);
            doc.text('Past 30 Days', 196 - doc.getTextWidth('Past 30 Days'), chartStartY - 2);
            chartStartY += 6;

            chartConfigs.forEach((cfg, idx) => {
                const col = idx % 2;
                const row = Math.floor(idx / 2);
                const cx = 14 + col * (chartW + 10);
                const cy = chartStartY + row * (chartH + 40);

                // Card background
                doc.setFillColor(250, 250, 252);
                doc.setDrawColor(230, 230, 235);
                doc.setLineWidth(0.3);
                doc.roundedRect(cx, cy, chartW, chartH + 34, 2, 2, 'FD');

                // Label
                doc.setFontSize(7);
                doc.setTextColor(100, 100, 110);
                doc.text(cfg.label, cx + 4, cy + 6);

                // Latest value (big, colored)
                const vals = filteredVitals.map(cfg.getValue);
                const vals2 = cfg.getValue2 ? filteredVitals.map(cfg.getValue2) : null;
                const latest = vals[vals.length - 1];
                const latestStr = vals2 ? `${latest}/${vals2[vals2.length - 1]}` : String(latest);

                doc.setFontSize(14);
                doc.setTextColor(cfg.color[0], cfg.color[1], cfg.color[2]);
                doc.text(latestStr, cx + 4, cy + 15);

                // Unit
                doc.setFontSize(7);
                doc.setTextColor(140, 140, 150);
                const valWidth = doc.getStringUnitWidth(latestStr) * 14 / doc.internal.scaleFactor;
                doc.text(cfg.unit, cx + 4 + valWidth + 1, cy + 15);

                // Graph area
                const gx = cx + 2;
                const gy = cy + 20;
                const gw = chartW - 4;
                const gh = chartH - 2;

                const allVals = vals2 ? [...vals, ...vals2] : vals;
                const minV = Math.min(...allVals);
                const maxV = Math.max(...allVals);
                const range = maxV - minV || 1;
                const pad = range * 0.15;
                const yMin = minV - pad;
                const yRange = range + pad * 2;

                const toY = (val: number) => gy + 2 + (1 - (val - yMin) / yRange) * (gh - 4);
                const toX = (i: number) => gx + (i / (dataPoints - 1)) * gw;

                // Draw gradient fill under the line (from line to bottom)
                const drawAreaFill = (values: number[], r: number, g: number, b: number) => {
                    const bottom = gy + gh;
                    for (let i = 0; i < dataPoints - 1; i++) {
                        const x1 = toX(i);
                        const x2 = toX(i + 1);
                        const y1 = toY(values[i]);
                        const y2 = toY(values[i + 1]);
                        const segW = x2 - x1;
                        const topOfSeg = Math.min(y1, y2);
                        const segH = bottom - topOfSeg;
                        if (segH <= 0) continue;
                        // Fade from color at top to white at bottom
                        const steps = 6;
                        for (let s = 0; s < steps; s++) {
                            const frac = s / steps;
                            const alpha = 0.2 * (1 - frac);
                            const fr = Math.round(255 - (255 - r) * alpha);
                            const fg = Math.round(255 - (255 - g) * alpha);
                            const fb = Math.round(255 - (255 - b) * alpha);
                            doc.setFillColor(fr, fg, fb);
                            const bandY = topOfSeg + (segH / steps) * s;
                            const bandH = segH / steps + 0.2;
                            doc.rect(x1, bandY, segW + 0.1, bandH, 'F');
                        }
                    }
                };

                drawAreaFill(vals, cfg.color[0], cfg.color[1], cfg.color[2]);

                // Draw line
                const drawLine = (values: number[], r: number, g: number, b: number, width: number) => {
                    doc.setDrawColor(r, g, b);
                    doc.setLineWidth(width);
                    for (let i = 1; i < dataPoints; i++) {
                        doc.line(toX(i - 1), toY(values[i - 1]), toX(i), toY(values[i]));
                    }
                };

                if (vals2) {
                    const lc = cfg.lightColor || [180, 180, 210];
                    drawAreaFill(vals2, lc[0], lc[1], lc[2]);
                    drawLine(vals2, lc[0], lc[1], lc[2], 0.5);
                }
                drawLine(vals, cfg.color[0], cfg.color[1], cfg.color[2], 0.8);

                // Date labels below graph
                doc.setFontSize(6);
                doc.setTextColor(140, 140, 150);
                const startLabel = new Date(filteredVitals[0].date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                const endLabel = new Date(filteredVitals[dataPoints - 1].date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                doc.text(startLabel, gx + 1, gy + gh + 4);
                doc.text(endLabel, gx + gw - doc.getTextWidth(endLabel) - 1, gy + gh + 4);

                // Graph legend label
                const legY = cy + 18;
                doc.setFontSize(5);
                if (vals2) {
                    doc.setFillColor(cfg.color[0], cfg.color[1], cfg.color[2]);
                    doc.circle(cx + chartW - 32, legY - 1, 1, 'F');
                    doc.setTextColor(100, 100, 110);
                    doc.text('Systolic', cx + chartW - 30, legY);
                    const lc = cfg.lightColor || [180, 180, 210];
                    doc.setFillColor(lc[0], lc[1], lc[2]);
                    doc.circle(cx + chartW - 16, legY - 1, 1, 'F');
                    doc.text('Diastolic', cx + chartW - 14, legY);
                } else {
                    doc.setFillColor(cfg.color[0], cfg.color[1], cfg.color[2]);
                    doc.setTextColor(100, 100, 110);
                    const lblText = cfg.label;
                    const lblW = doc.getTextWidth(lblText);
                    doc.circle(cx + chartW - lblW - 6, legY - 1, 1, 'F');
                    doc.text(lblText, cx + chartW - lblW - 4, legY);
                }

                // Divider
                const divY = gy + gh + 7;
                doc.setDrawColor(235, 235, 240);
                doc.setLineWidth(0.2);
                doc.line(cx + 3, divY, cx + chartW - 3, divY);

                // Min / Avg / Max in one line
                const statsY = divY + 4;
                const avg = Math.round(vals.reduce((a, b) => a + b, 0) / vals.length);
                const min = Math.round(Math.min(...vals));
                const max = Math.round(Math.max(...vals));

                let statsLine: string;
                if (vals2) {
                    const avg2 = Math.round(vals2.reduce((a, b) => a + b, 0) / vals2.length);
                    const min2 = Math.round(Math.min(...vals2));
                    const max2 = Math.round(Math.max(...vals2));
                    statsLine = `Min: ${min}/${min2}  |  Avg: ${avg}/${avg2}  |  Max: ${max}/${max2}`;
                } else {
                    statsLine = `Min: ${min}  |  Avg: ${avg}  |  Max: ${max}`;
                }
                doc.setFontSize(5.5);
                doc.setTextColor(120, 120, 130);
                doc.text(statsLine, cx + chartW / 2 - doc.getTextWidth(statsLine) / 2, statsY);
            });
        }

        const tableStartY = dataPoints > 1 ? chartStartY + 2 * (chartH + 40) + 6 : 50;

        // --- Vitals table ---
        const tableData = filteredVitals.map(v => [
            new Date(v.date).toLocaleDateString(),
            `${v.bp.systolic}/${v.bp.diastolic}`,
            v.hr,
            v.glucose,
            v.weight,
        ]);

        autoTable(doc, {
            startY: tableStartY,
            head: [['Date', 'BP (mmHg)', 'Heart Rate (bpm)', 'Glucose (mg/dL)', 'Weight (kg)']],
            body: tableData,
            headStyles: { fillColor: [90, 70, 170] },
            styles: { fontSize: 8, cellPadding: 3 },
        });
    }

    doc.save(`health_report_${patient.id}_${new Date().toISOString().split('T')[0]}.pdf`);
    setIsGenerating(false);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-30" role="dialog" aria-modal="true">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg p-6 m-4 transform transition-all">
        <div className="flex items-center justify-between pb-4 border-b border-light-gray">
          <h2 className="text-xl font-bold text-primary" id="export-modal-title">Export Health Data</h2>
          <button
            onClick={onClose}
            className="p-2 rounded-full text-gray-500 hover:bg-light-gray hover:text-gray-800"
            aria-label="Close modal"
          >
             <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="py-6 space-y-6">
          <div>
            <label className="text-sm font-semibold text-gray-600 mb-2 block">Export Type</label>
            <div className="flex gap-2">
              <button
                onClick={() => { setExportType('records'); setShowMonthlyReport(false); }}
                className={`flex-1 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${exportType === 'records' ? 'bg-primary text-white shadow' : 'bg-light-gray text-gray-700 hover:bg-primary-light'}`}
                aria-pressed={exportType === 'records'}
              >
                All Health Records
              </button>
              <button
                onClick={() => { setExportType('vitals'); setShowMonthlyReport(false); }}
                className={`flex-1 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${exportType === 'vitals' ? 'bg-primary text-white shadow' : 'bg-light-gray text-gray-700 hover:bg-primary-light'}`}
                aria-pressed={exportType === 'vitals'}
              >
                Vital Signs Summary
              </button>
              <button
                onClick={() => { setExportType('monthly'); setShowMonthlyReport(false); }}
                className={`flex-1 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${exportType === 'monthly' ? 'bg-primary text-white shadow' : 'bg-light-gray text-gray-700 hover:bg-primary-light'}`}
                aria-pressed={exportType === 'monthly'}
              >
                Monthly Report
              </button>
            </div>
          </div>
          
          {exportType !== 'monthly' && (
          <div>
            <label htmlFor="date-range-select" className="text-sm font-semibold text-gray-600 mb-2 block">Date Range</label>
            <select
                id="date-range-select"
                value={dateRange}
                onChange={e => setDateRange(e.target.value)}
                className="w-full p-2 border border-gray-300 rounded-md bg-white focus:ring-primary-light focus:border-primary"
            >
                <option value="all">All Time</option>
                <option value="7days">Last 7 Days</option>
                <option value="30days">Last 30 Days</option>
                <option value="custom">Custom Range</option>
            </select>
          </div>
          )}

          {exportType === 'monthly' && !showMonthlyReport && (
          <div>
            <label htmlFor="month-select" className="text-sm font-semibold text-gray-600 mb-2 block">Select Month</label>
            <input
              type="month"
              id="month-select"
              value={selectedMonth}
              onChange={e => setSelectedMonth(e.target.value)}
              className="w-full p-2 border border-gray-300 rounded-md bg-white focus:ring-primary-light focus:border-primary"
            />
          </div>
          )}

          {dateRange === 'custom' && (
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label htmlFor="start-date" className="text-sm font-medium text-gray-600 mb-1 block">Start Date</label>
                <input
                  type="date"
                  id="start-date"
                  value={customStartDate}
                  onChange={e => setCustomStartDate(e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded-md bg-white focus:ring-primary-light focus:border-primary"
                />
              </div>
              <div>
                <label htmlFor="end-date" className="text-sm font-medium text-gray-600 mb-1 block">End Date</label>
                <input
                  type="date"
                  id="end-date"
                  value={customEndDate}
                  onChange={e => setCustomEndDate(e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded-md bg-white focus:ring-primary-light focus:border-primary"
                />
              </div>
            </div>
          )}
        </div>

        <div className="pt-4 border-t border-light-gray flex justify-end gap-3">
          <button
            type="button"
            onClick={() => { setShowMonthlyReport(false); onClose(); }}
            className="px-6 py-2 rounded-lg font-medium bg-light-gray text-gray-700 hover:bg-gray-200 transition"
          >
            Cancel
          </button>
          {exportType === 'monthly' ? (
            <button
              type="button"
              onClick={() => setShowMonthlyReport(true)}
              disabled={!selectedMonth}
              className="px-6 py-2 rounded-lg font-medium bg-accent text-white hover:bg-orange-600 transition disabled:opacity-50 disabled:cursor-wait"
            >
              Generate Monthly Report
            </button>
          ) : (
            <button
              type="button"
              onClick={handleGeneratePdf}
              disabled={isGenerating}
              className="px-6 py-2 rounded-lg font-medium bg-accent text-white hover:bg-orange-600 transition disabled:opacity-50 disabled:cursor-wait"
            >
              {isGenerating ? 'Generating...' : 'Generate PDF'}
            </button>
          )}
        </div>
      </div>

      {/* Monthly Report Full-Screen View */}
      {showMonthlyReport && (
        <div className="fixed inset-0 bg-white z-50 overflow-auto">
          <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-3 flex items-center justify-between z-10">
            <h2 className="text-lg font-bold text-primary">Monthly Health Report</h2>
            <button
              onClick={() => setShowMonthlyReport(false)}
              className="px-4 py-2 rounded-lg font-medium bg-light-gray text-gray-700 hover:bg-gray-200 transition"
            >
              ← Back to Export
            </button>
          </div>
          <div className="p-6">
            <MonthlyReportPage
              customerId={patient.id}
              year={parseInt(selectedMonth.split('-')[0], 10)}
              month={parseInt(selectedMonth.split('-')[1], 10)}
              patientName={patient.name}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default ExportModal;